import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, Package, Truck, CheckCircle, Clock, Phone, MapPin, Loader2, Navigation } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface DeliveryOrder {
  id: string;
  buyerId: string;
  status: string;
  paymentStatus: string;
  totalAmount: number;
  deliveryFee: number;
  createdAt: Date;
  items: any[];
  shippingAddress: any;
  deliveryAddress: string | null;
  customerName: string;
  customerPhone: string;
  trackingNumber: string | null;
  estimatedDelivery: string | null;
  deliveryNotes: string | null;
  sellerName?: string;
  sellerId?: string;
}

interface SelfDeliveryManagementProps {
  isAdmin?: boolean;
}

export function SelfDeliveryManagement({ isAdmin = false }: SelfDeliveryManagementProps) {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [orders, setOrders] = useState<DeliveryOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<DeliveryOrder | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");

  useEffect(() => {
    if (user?.id) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    if (!user?.id) return;
    
    try {
      let query = supabase
        .from('orders')
        .select('*')
        .in('status', ['confirmed', 'shipped', 'processing'])
        .eq('payment_status', 'completed')
        .order('created_at', { ascending: false });

      if (isAdmin) {
        // Admin sees all self-delivery orders (tracking_number starts with 'SELF-')
        query = query.like('tracking_number', 'SELF-%');
      } else {
        // Check if seller uses self-delivery
        const { data: profile } = await supabase
          .from('profiles')
          .select('delivery_preference')
          .eq('id', user.id)
          .single();

        if (profile?.delivery_preference !== 'self') {
          setOrders([]);
          setLoading(false);
          return;
        }
        query = query.eq('seller_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        const buyerIds = Array.from(new Set(data.map((o: any) => o.buyer_id).filter(Boolean)));
        const sellerIds = isAdmin ? Array.from(new Set(data.map((o: any) => o.seller_id).filter(Boolean))) : [];
        let buyersMap: Record<string, any> = {};
        let sellersMap: Record<string, any> = {};
        
        if (buyerIds.length > 0) {
          const { data: buyers } = await supabase
            .from('profiles')
            .select('id, full_name, username, phone')
            .in('id', buyerIds);
          buyersMap = (buyers || []).reduce((acc: any, b: any) => {
            acc[b.id] = b;
            return acc;
          }, {});
        }

        if (isAdmin && sellerIds.length > 0) {
          const { data: sellers } = await supabase
            .from('profiles')
            .select('id, full_name, business_name')
            .in('id', sellerIds);
          sellersMap = (sellers || []).reduce((acc: any, s: any) => {
            acc[s.id] = s;
            return acc;
          }, {});
        }

        setOrders(data.map((order: any) => {
          // Parse shipping_address if it's a JSON string
          const shippingAddress = typeof order.shipping_address === 'string' 
            ? JSON.parse(order.shipping_address) 
            : order.shipping_address;

          // Prioritize recipient_name/recipient_phone columns, then fallback to shipping_address, then profile
          const customerName = order.recipient_name || 
                              shippingAddress?.fullName || 
                              shippingAddress?.name ||
                              buyersMap[order.buyer_id]?.full_name || 
                              buyersMap[order.buyer_id]?.username || 
                              'Guest Customer';

          const customerPhone = order.recipient_phone || 
                               shippingAddress?.phone || 
                               buyersMap[order.buyer_id]?.phone || 
                               'N/A';

          // Parse delivery_address - could be string or JSON
          let deliveryAddressObj = order.delivery_address;
          if (typeof deliveryAddressObj === 'string') {
            try {
              deliveryAddressObj = JSON.parse(deliveryAddressObj);
            } catch {
              // Keep as string
            }
          }

          return {
            id: order.id,
            buyerId: order.buyer_id,
            sellerId: order.seller_id,
            status: order.status || 'pending',
            paymentStatus: order.payment_status || 'pending',
            totalAmount: Number(order.total_amount),
            deliveryFee: Number(order.delivery_fee || 0),
            createdAt: new Date(order.created_at),
            items: order.items || [],
            shippingAddress: shippingAddress || deliveryAddressObj,
            deliveryAddress: typeof deliveryAddressObj === 'string' ? deliveryAddressObj : null,
            customerName,
            customerPhone,
            trackingNumber: order.tracking_number,
            estimatedDelivery: order.estimated_delivery,
            deliveryNotes: null,
            sellerName: sellersMap[order.seller_id]?.business_name || sellersMap[order.seller_id]?.full_name || 'Unknown Seller'
          };
        }));
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const openUpdateModal = (order: DeliveryOrder) => {
    setSelectedOrder(order);
    setDeliveryStatus(order.status);
    setDeliveryNotes("");
    setIsUpdateModalOpen(true);
  };

  const handleUpdateDelivery = async () => {
    if (!selectedOrder) return;

    setUpdateLoading(true);
    try {
      // Use server-side validated RPC for all self-delivery status updates
      const { data: result, error: rpcError } = await supabase
        .rpc('update_self_delivery_status', {
          p_order_id: selectedOrder.id,
          p_new_status: deliveryStatus,
          p_delivery_notes: deliveryNotes || getStatusDescription(deliveryStatus),
        });

      if (rpcError) throw rpcError;

      const rpcResult = result as any;
      if (!rpcResult?.success) {
        toast.error(rpcResult?.message || "Self-delivery is not enabled for this seller");
        setUpdateLoading(false);
        return;
      }

      // Send WhatsApp notification to customer
      if (selectedOrder.customerPhone && selectedOrder.customerPhone !== 'N/A') {
        try {
          await supabase.functions.invoke('send-whatsapp-notification', {
            body: {
              type: 'delivery-status-update',
              to: selectedOrder.customerPhone,
              data: {
                orderNumber: selectedOrder.id.slice(0, 8).toUpperCase(),
                status: getStatusLabel(deliveryStatus),
                statusMessage: deliveryNotes || getStatusDescription(deliveryStatus),
                customerName: selectedOrder.customerName,
                trackingUrl: `https://sabitradeng.com/track-order/${selectedOrder.id}`
              }
            }
          });
          toast.success(`Customer notified via WhatsApp`);
        } catch (notifError) {
          console.error('Failed to send WhatsApp notification:', notifError);
          // Don't fail the whole operation if notification fails
        }
      }

      toast.success(`Order marked as ${getStatusLabel(deliveryStatus)}`);
      setIsUpdateModalOpen(false);
      await fetchOrders();
    } catch (error) {
      console.error('Error updating delivery:', error);
      toast.error("Failed to update delivery status");
    } finally {
      setUpdateLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmed';
      case 'processing': return 'Processing';
      case 'shipped': return 'Out for Delivery';
      case 'delivered': return 'Delivered';
      default: return status;
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Order confirmed, preparing for delivery';
      case 'processing': return 'Order is being prepared';
      case 'shipped': return 'Out for delivery';
      case 'delivered': return 'Order has been delivered to customer';
      default: return 'Status updated';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "confirmed":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Confirmed</Badge>;
      case "processing":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Processing</Badge>;
      case "shipped":
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Out for Delivery</Badge>;
      case "delivered":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Delivered</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const openWhatsApp = (phone: string, orderInfo: string) => {
    const message = encodeURIComponent(`Hello! I'm contacting you about your order ${orderInfo}. `);
    const cleanPhone = phone.replace(/\D/g, '');
    const formattedPhone = cleanPhone.startsWith('234') ? cleanPhone : `234${cleanPhone.replace(/^0/, '')}`;
    window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.customerPhone.includes(searchQuery);
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <p>Loading deliveries...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Self-Delivery Management
            </CardTitle>
            <CardDescription>
              {isAdmin ? "Monitor all seller self-delivery orders" : "Track and update your self-delivery orders"}
            </CardDescription>
          </div>
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Out for Delivery</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                className="pl-10 w-48"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredOrders.length === 0 ? (
          <div className="text-center py-10">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-muted-foreground">No orders to deliver</p>
            <p className="text-sm text-muted-foreground mt-1">Orders ready for self-delivery will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <div key={order.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex flex-col md:flex-row justify-between gap-3">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-muted rounded-lg">
                      <Package className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">#{order.id.slice(0, 8).toUpperCase()}</span>
                        {getStatusBadge(order.status)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {order.createdAt.toLocaleDateString()} • {order.items.length} item(s) • ₦{order.totalAmount.toLocaleString()}
                        {isAdmin && order.sellerName && (
                          <span className="ml-2">• Seller: <span className="font-medium">{order.sellerName}</span></span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openWhatsApp(order.customerPhone, `#${order.id.slice(0, 8).toUpperCase()}`)}
                    >
                      <WhatsAppIcon className="h-4 w-4 mr-1" size={16} />
                      Contact
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openUpdateModal(order)}
                    >
                      <Navigation className="h-4 w-4 mr-1" />
                      Update Status
                    </Button>
                  </div>
                </div>

                {/* Customer & Delivery Info */}
                <div className="grid md:grid-cols-2 gap-4 pt-3 border-t">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Customer</p>
                    <p className="text-sm">{order.customerName}</p>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      {order.customerPhone}
                    </div>
                  </div>
                  {(order.shippingAddress || order.deliveryAddress) && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Delivery Address</p>
                      <div className="flex items-start gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>
                          {order.deliveryAddress || (
                            order.shippingAddress && typeof order.shippingAddress === 'object' 
                              ? [
                                  order.shippingAddress.address || order.shippingAddress.addressLine1,
                                  order.shippingAddress.city,
                                  order.shippingAddress.state
                                ].filter(Boolean).join(', ')
                              : 'Address not available'
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Items */}
                <div className="pt-3 border-t">
                  <p className="text-sm font-medium mb-2">Items</p>
                  <div className="flex flex-wrap gap-2">
                    {order.items.map((item: any, idx: number) => (
                      <Badge key={idx} variant="secondary">
                        {item.quantity}x {item.productName || item.product?.title || 'Item'}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Update Delivery Status Modal */}
      <Dialog open={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Delivery Status</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-mono text-sm">Order #{selectedOrder.id.slice(0, 8).toUpperCase()}</p>
                <p className="text-sm text-muted-foreground">{selectedOrder.customerName} • {selectedOrder.customerPhone}</p>
              </div>

              <div className="space-y-2">
                <Label>Delivery Status</Label>
                <Select value={deliveryStatus} onValueChange={setDeliveryStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-500" />
                        Confirmed (Preparing)
                      </div>
                    </SelectItem>
                    <SelectItem value="processing">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-yellow-500" />
                        Processing
                      </div>
                    </SelectItem>
                    <SelectItem value="shipped">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-purple-500" />
                        Out for Delivery
                      </div>
                    </SelectItem>
                    <SelectItem value="delivered">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Delivered
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Delivery Notes (Optional)</Label>
                <Textarea
                  placeholder="e.g., Left with security, Customer collected at gate..."
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUpdateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateDelivery} disabled={updateLoading}>
              {updateLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Status'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default SelfDeliveryManagement;
